/**
 * Compact field template (FEAT-2026-0005, Phase 2 polish + Phase 8 form-density).
 *
 * Wraps every field with a tiny caption-style label above the control,
 * replaces the default ~24-px-tall MUI label + helper-text block with a
 * ~14-px caption, and uses a tighter mb=0.5 between fields.
 *
 * Object/array fields skip the label here — their wrapping
 * ObjectFieldTemplate / ArrayFieldTemplate provides the section heading
 * instead, so we don't double up.
 *
 * Phase 8: when this field is rendered as one item of an
 * array-of-primitives (the "xTickPositions: [10, 20, 25, …]" case), the
 * outer ArrayFieldTemplate sets `PrimitiveArrayItemContext = true` and
 * we drop the label / margin entirely so the items can sit inline at
 * ~70 px wide.
 */

import { useContext } from 'react';
import { Box, Typography } from '@mui/material';
import type { FieldTemplateProps } from '@rjsf/utils';
import { PrimitiveArrayItemContext } from './FormSearchContext';

export const FieldTemplate = (props: FieldTemplateProps) => {
  const {
    id, classNames, style, label, help, required, description, errors, children, hidden,
    schema, displayLabel,
  } = props;
  const inPrimitiveArray = useContext(PrimitiveArrayItemContext);

  if (hidden) {
    return <div style={{ display: 'none' }}>{children}</div>;
  }

  const isObject = schema.type === 'object';
  const isArray  = schema.type === 'array';
  const showLabel = displayLabel
    && !isObject && !isArray
    && !!label
    && !inPrimitiveArray;

  return (
    <Box
      className={classNames}
      style={style}
      sx={{ mb: inPrimitiveArray ? 0 : 0.5 }}
    >
      {showLabel && (
        <Typography
          component="label"
          htmlFor={id}
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.25,
            fontSize: '0.72rem',
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.2,
          }}
        >
          {label}{required ? ' *' : ''}
        </Typography>
      )}
      {description}
      {children}
      {!inPrimitiveArray && errors}
      {!inPrimitiveArray && help}
    </Box>
  );
};
